import React, { useCallback, type SyntheticEvent } from 'react';
import noop from 'lodash/noop';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import Link from '@atlaskit/link';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import {
	withFlagService,
	toFlagId,
	dismissFlag as dismissFlagCommand,
	type FlagService,
} from '@atlassian/jira-flags';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	fireTrackAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsJCS } from '@atlassian/jira-servicedesk-customer-service-common/src/utils/use-is-jcs';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useSoftDeleteService } from '../services';
import messages from './messages';

export type Props = {
	isProjectSimplified: boolean;
	projectName: string;
	projectType: ProjectType | undefined;
	baseUrl?: string;
	projectId: number | string;
	onDeleteProjectSuccess: (replace: (href: string) => void) => void;
	onDeleteProjectFailure: (arg1: Error | undefined) => void;
	onClose: () => void;
	onDeleteSuccessFlagRestore?: () => void;
	dropDownMenuButtonRef?: RefObject;
};

type PropsWithIntlAndFlagService = Props & {
	intl: IntlShape;
	flagService: FlagService;
};

const TRASH_AND_RESTORE_HELP_LINK =
	'https://support.atlassian.com/jira-service-management-cloud/docs/trash-and-restore-a-project/';

export const SoftDeleteProjectModal = (props: PropsWithIntlAndFlagService) => {
	const {
		projectName,
		baseUrl = '',
		projectId,
		projectType,
		isProjectSimplified,
		intl: { formatMessage },
		onClose,
		onDeleteProjectSuccess,
		onDeleteProjectFailure,
		onDeleteSuccessFlagRestore,
		flagService: { showFlag, dismissFlag },
		dropDownMenuButtonRef,
	} = props;
	const [isDeleting, softDelete] = useSoftDeleteService({
		baseUrl,
		projectId,
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attributes = {
		projectType,
		isProjectSimplified,
	};
	const isAdmin = useIsAdmin();
	const { replace } = useRouterActions();
	const isJCS = useIsJCS();

	const getActions = useCallback(
		(flagId: string) => {
			if (!isAdmin) {
				return [];
			}
			if (!onDeleteSuccessFlagRestore) {
				return [
					{
						content: messages.deleteSuccessGoToTrashButtonTitle,
						href: '/jira/settings/projects/trash',
					},
				];
			}
			return [
				{
					content: messages.deleteSuccessGoToTrashButtonTitle,
					href: '/jira/settings/projects/trash',
				},
				{
					content: messages.deleteSuccessRestoreButtonTitle,
					onClick: () => {
						onDeleteSuccessFlagRestore();
						flagId && dismissFlag(dismissFlagCommand(flagId));
					},
				},
			] as const;
		},
		[dismissFlag, isAdmin, onDeleteSuccessFlagRestore],
	);

	const onSuccess = (): void => {
		const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
			action: 'movedToTrash',
		});
		fireTrackAnalytics(analyticsEvent, 'project movedToTrash', projectId.toString(), attributes);
		const flagId = toFlagId(`trash-${projectId}`);
		showFlag({
			id: flagId,
			type: 'success',
			title:
				projectType === SERVICE_DESK_PROJECT
					? messages.deleteSuccessServiceDeskMessageTitle
					: messages.deleteSuccessMessageTitle,
			testId: 'project-soft-delete-modal.ui.flags.moved-to-trash-success',
			isAutoDismiss: false,
			// @ts-expect-error - TS2322 - Type '{ content: MessageDescriptorV2; href: string; }[] | readonly [{ readonly content: MessageDescriptorV2; readonly href: "/jira/settings/projects/trash"; }, { ...; }]' is not assignable to type 'FlagAction[] | undefined'.
			actions: getActions(flagId),
		});
		onDeleteProjectSuccess(replace);
	};

	const onDocumentationClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'link clicked',
			'moveToTrashProjectModalDocumentation',
		);
	};

	const onFailure = (error: Error): void => {
		onDeleteProjectFailure(error);
		showFlag({
			type: 'error',
			title: [messages.deleteErrorMessageTitle, { projectName }],
			description: [messages.deleteErrorMessageDescription, { projectName }],
			testId: 'project-soft-delete-modal.ui.flags.moved-to-trash-failure',
			isAutoDismiss: false,
		});
	};

	const onDelete = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'moveToTrashProjectButton');
		softDelete({ onSuccess, onFailure });
	};

	// TODO: BERRY-3000 - Only show this error flag when the project is JCS and associated with a Help Center (i.e. hoisted)
	const onDeleteCustomerServiceProject = (
		e: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
	): void => {
		fireUIAnalytics(analyticsEvent, 'moveToTrashProjectButton');
		onDeleteProjectFailure(undefined);
		showFlag({
			type: 'error',
			title: [messages.deleteCustomerServiceErrorMessageTitleNonFinal, { projectName }],
			description: [messages.deleteCustomerServiceErrorMessageDescriptionNonFinal, { projectName }],
			testId: 'project-soft-delete-modal.ui.flags.customer-service-moved-to-trash-failure',
			isAutoDismiss: false,
		});
	};

	const isServiceDeskProject = (): boolean => projectType === SERVICE_DESK_PROJECT;

	const renderServiceDeskTrashModalContent = () => (
		<>
			<p>{formatMessage(messages.explainWhatDeleteDoesPart1ServiceDesk)}</p>
			<p>
				{formatMessage(messages.explainWhatDeleteDoesPart2ServiceDesk)}&nbsp;
				<Link href={TRASH_AND_RESTORE_HELP_LINK} target="_blank" onClick={onDocumentationClick}>
					{formatMessage(messages.learnMoreAboutTrashAndRestore)}
				</Link>
			</p>
		</>
	);

	const renderTrashModalContent = () => (
		<>
			<p>{formatMessage(messages.explainWhatDeleteDoesPart1)}</p>
			<p>{formatMessage(messages.explainWhatDeleteDoesPart2)}</p>
		</>
	);

	const cancelActionWithAnalytics = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'cancelProjectTrashModal', {
			projectType,
		});
		onClose();
	};

	const onCancelOrNoop = !isDeleting ? cancelActionWithAnalytics : noop;

	return (
		<ContextualAnalyticsData
			sourceName="moveToTrashProject"
			sourceType={MODAL}
			attributes={attributes}
			containerId={`${projectId}`}
			containerType="project"
		>
			<ShortcutScope>
				<ModalDialog
					width="small"
					onClose={onCancelOrNoop}
					autoFocus
					shouldReturnFocus={dropDownMenuButtonRef}
				>
					<ModalHeader>
						<ModalTitle appearance="danger">
							{formatMessage(messages.modalHeader, { projectName })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{isServiceDeskProject()
							? renderServiceDeskTrashModalContent()
							: renderTrashModalContent()}
					</ModalBody>
					<ModalFooter>
						<ButtonGroup>
							<Button
								appearance="subtle-link"
								onClick={onCancelOrNoop}
								testId="project-soft-delete-modal.ui.close-button-wrapper"
								isDisabled={isDeleting}
							>
								{formatMessage(messages.cancelDeleteButton)}
							</Button>
							<Button
								appearance="danger"
								onClick={isJCS ? onDeleteCustomerServiceProject : onDelete}
								testId="project-soft-delete-modal.ui.move-to-trash-button-wrapper"
								isDisabled={isDeleting}
							>
								{isServiceDeskProject()
									? formatMessage(messages.confirmDeleteButtonServiceDesk)
									: formatMessage(messages.confirmDeleteButton)}
							</Button>
						</ButtonGroup>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export const defaultProps = {
	isDeleting: false,
	projectName: 'Soft Delete Project Name',
	onDelete: noop,
	onClose: noop,
} as const;

export default injectIntl(withFlagService(SoftDeleteProjectModal));
